import { Avatar } from "./ui/Avatar";
import { TimeAgo } from "./TimeAgo";
import type { ThreadComment } from "./CommentItem";

/** The comment's text without its markup — the images it carries are what the
    lightbox is already showing, so the sidebar only needs the words. */
function commentPlainText(html: string): string {
  return html
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Sidebar for the comment lightbox: who wrote it, when, and what it said.
    A comment has no post panel, so this replaces ImageLightbox's default. */
export function CommentLightboxSidebar({
  comment,
  bodyHtml,
}: {
  comment: ThreadComment;
  bodyHtml: string;
}) {
  // An anonymous author the server did not de-anonymise for us is simply
  // `null` — there is no `"anonym"` sentinel to compare against (O-21).
  const isAnon = comment.isAnonymous && comment.author === null;
  const name = comment.author?.name ?? "Anonym";
  const text = commentPlainText(bodyHtml);

  return (
    <div className="px-4 pt-4 pb-3 lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
      <div className="flex items-center gap-2.5">
        <Avatar
          url={isAnon ? "" : (comment.author?.avatar.medium ?? "")}
          name={isAnon ? "Anonym" : name}
          size="sm"
        />
        <div>
          <p className="font-semibold text-sm text-foreground">
            {isAnon ? "Anonym" : name}
          </p>
          <TimeAgo date={comment.createdAt} />
        </div>
      </div>
      {text && (
        <p className="mt-3 text-muted-foreground text-sm leading-relaxed whitespace-pre-wrap">
          {text}
        </p>
      )}
    </div>
  );
}
